import { __read } from "tslib";
import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import ReactDOM from 'react-dom';
import { useForkRef } from '@material-ui/core/utils';
import { logInDev, RcPortalWindowContext, useEventCallback, useThemeProps, } from '../../foundation';
import { RcDetachedWindowStylesProvider } from './DetachedWindowStylesProvider';
import { GlobalStyle } from './styles';
import { addCommentInHeader, DetachedWindowRefMaps, MUI_INSERTION_POINT, openExternalWindow, } from './utils';
/**
 * That can make render element on new window, and can shared state easier
 * implementation follow `window.open` api
 * https://www.w3schools.com/jsref/met_win_open.asp
 */
var RcDetachedWindow = memo(forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcDetachedWindow' });
    var childrenProp = props.children, onBeforeunload = props.onBeforeunload, onUnload = props.onUnload, onload = props.onload, title = props.title, url = props.url, name = props.name, specs = props.specs, open = props.open, onOpen = props.onOpen, getWindow = props.getWindow, keep = props.keep;
    var _a = __read(useState(), 2), externalWindow = _a[0], setExternalWindow = _a[1];
    var isCloseByWindowExitRef = useRef(false);
    var innerRef = useRef(null);
    var instanceRef = useForkRef(ref, innerRef);
    var beforeMainUnload = useEventCallback(function () {
        if (externalWindow) {
            closeWindow(externalWindow);
        }
    });
    var closeWindow = useEventCallback(function (externalWindow, isState) {
        if (isState === void 0) { isState = true; }
        // * remember remove main window listener beforeunload when close window
        window.removeEventListener('beforeunload', beforeMainUnload);
        if (!isCloseByWindowExitRef.current)
            externalWindow.close();
        isCloseByWindowExitRef.current = false;
        if (isState) {
            setExternalWindow(undefined);
        }
    });
    var openWindow = useEventCallback(function () {
        var newWindow = getWindow
            ? getWindow({ url: url, name: name, specs: specs, title: title })
            : openExternalWindow(props);
        if (!newWindow) {
            if (process.env.NODE_ENV !== 'production') {
                logInDev({
                    component: 'RcDetachedWindow',
                    message: 'Please check your window instance, that `window.open` should not trigger by another window',
                });
            }
            return;
        }
        if (title)
            newWindow.document.title = title;
        addCommentInHeader(newWindow);
        if (onload)
            newWindow.onload = onload;
        if (onUnload)
            newWindow.addEventListener('unload', onUnload);
        newWindow.addEventListener('beforeunload', function (e) {
            isCloseByWindowExitRef.current = true;
            onBeforeunload === null || onBeforeunload === void 0 ? void 0 : onBeforeunload(e);
        });
        onOpen === null || onOpen === void 0 ? void 0 : onOpen(newWindow);
        setExternalWindow(newWindow);
        // * also need listen when main window close
        window.addEventListener('beforeunload', beforeMainUnload);
        return newWindow;
    });
    useEffect(function () {
        DetachedWindowRefMaps.set(innerRef, true);
        var newWindow;
        if (open) {
            newWindow = openWindow();
        }
        else if (externalWindow) {
            closeWindow(externalWindow);
        }
        return function () {
            DetachedWindowRefMaps.delete(innerRef);
            // * when window is open, and destroy mean that trigger by whole component destroy
            if (open && newWindow) {
                closeWindow(newWindow, false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    useImperativeHandle(instanceRef, function () { return ({
        window: externalWindow,
    }); }, [externalWindow]);
    var children = useMemo(function () {
        if (!(externalWindow === null || externalWindow === void 0 ? void 0 : externalWindow.document.body))
            return null;
        return (React.createElement(RcDetachedWindowStylesProvider, { jssInsertionPoint: MUI_INSERTION_POINT, externalWindow: externalWindow },
            React.createElement(RcPortalWindowContext.Provider, { value: {
                    externalWindow: externalWindow,
                    document: (externalWindow === null || externalWindow === void 0 ? void 0 : externalWindow.document) || document,
                } },
                React.createElement(GlobalStyle, null),
                childrenProp)));
    }, [childrenProp, externalWindow]);
    if (!open && keep)
        return childrenProp;
    if (externalWindow) {
        return ReactDOM.createPortal(children, externalWindow.document.body);
    }
    return null;
}));
RcDetachedWindow.displayName = 'RcDetachedWindow';
export { RcDetachedWindow };
